import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from "framer-motion";
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import ContactSection from "../../components/ContactSection";

const ProfessionalServices = () => {

    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            id: '01',
            title: 'Technology Lifecycle Management Assessment',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            ),
            description: 'We evaluate your IT assets across their entire lifecycle to determine performance, security posture, and upgrade readiness. Our assessment identifies outdated systems, inefficiencies, and hidden risks that can impact operations. By aligning infrastructure with current standards and future business goals, we help organisations reduce downtime, control costs, and maintain technology relevance.',
            accent: '#2563eb'
        },
        {
            id: '02',
            title: 'Passive Cabling Site Survey',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            description: 'Our passive cabling survey assesses existing and greenfield infrastructure to ensure it supports current and future network demands. We evaluate physical layouts, cabling quality, and compliance with industry standards. This prevents performance bottlenecks, reduces future rework, and enables reliable expansion.',
            accent: '#0ea5e9'
        },
        {
            id: '03',
            title: 'Wireless LAN Site Assessment',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M1.42 9a16 16 0 0 1 21.16 0M5 12.55a11 11 0 0 1 14.08 0M10.54 16.1a6 6 0 0 1 2.92 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="20" r="1" fill="currentColor" /></svg>
            ),
            description: 'We conduct detailed RF analysis and wireless surveys to optimise coverage, capacity, and performance. Our assessments identify interference zones, signal gaps, and security vulnerabilities. This ensures consistent connectivity, enhanced user experience, and secure wireless operations.',
            accent: '#6366f1'
        },
        {
            id: '04',
            title: 'Network Architecture & Design',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="5" r="3" stroke="currentColor" strokeWidth="2" /><circle cx="5" cy="19" r="3" stroke="currentColor" strokeWidth="2" /><circle cx="19" cy="19" r="3" stroke="currentColor" strokeWidth="2" /><path d="M12 8v4M12 12l-5 4M12 12l5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            ),
            description: 'Solaris architects resilient, high-performance network designs tailored to your organisation\'s scale, security requirements, and growth objectives. From core switching and routing to SD-WAN and zero-trust segmentation, we deliver detailed design documentation, validation testing, and implementation roadmaps that set your infrastructure up for long-term success.',
            accent: '#f59e0b'
        },
        {
            id: '05',
            title: 'Security Posture Assessment',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
            ),
            description: 'Our security posture assessments provide a structured evaluation of your current cybersecurity controls, policies, and vulnerabilities. We benchmark your environment against industry frameworks including ISO 27001, NIST, and CIS - identifying gaps and delivering a prioritised remediation roadmap to strengthen your security stance and reduce organisational risk.',
            accent: '#10b981'
        },
        {
            id: '06',
            title: 'IT Strategy & Roadmap Consulting',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            description: 'Solaris provides expert IT strategy consulting that aligns technology investments with your business objectives. We analyse your current environment, benchmark against industry peers, and develop a clear, actionable technology roadmap - helping leadership make informed decisions that drive efficiency, reduce risk, and support sustainable growth.',
            accent: '#ef4444'
        }
    ];

    const servicesSpectrum = [
        'Network architecture design and access controls',
        'Switching and wireless optimisation',
        'Vulnerability assessment and managed security',
        'Threat intelligence and monitoring',
        'Email security and domain protection',
        'Data backup, encryption, and compliance',
        'Cloud performance and monitoring',
        'Identity and access management',
        'Technology lifecycle assessments',
        'IT strategy and roadmap consulting'
    ];

    const caseStudies = [
        {
            tag: 'Professional Services',
            title: 'Network Architecture Redesign Delivering 60% Improvement in Throughput',
            image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Professional Services',
            title: 'Wireless LAN Optimisation Across 12-Site Enterprise Campus Environment',
            image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&auto=format&fit=crop&q=80',
            dark: false
        },
        {
            tag: 'Professional Services',
            title: 'Security Posture Assessment Leading to Full ISO 27001 Alignment',
            image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Professional Services',
            title: 'IT Strategy Roadmap Enabling Digital Transformation for Global Manufacturer',
            image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&auto=format&fit=crop&q=80',
            dark: false
        }
    ];

    const faqs = [
        {
            question: 'What is included in a Professional Services engagement?',
            answer: 'A Solaris Professional Services engagement typically includes an initial discovery and scoping workshop, a structured assessment or design phase, detailed deliverables such as reports, architecture documents, or implementation plans, and a findings presentation with prioritised recommendations. The exact scope is tailored to your objectives - whether that is an infrastructure assessment, network design, security review, or full IT strategy consultation.'
        },
        {
            question: 'How long does an assessment typically take?',
            answer: 'Engagement duration varies depending on the scope and complexity of your environment. A focused site survey or single-domain assessment can typically be completed within one to two weeks. Broader engagements such as enterprise-wide technology lifecycle assessments or IT strategy roadmaps may span four to eight weeks. We define a clear timeline during the scoping phase so you know exactly what to expect.'
        },
        {
            question: 'Can services be customised?',
            answer: 'Absolutely. All Solaris Professional Services engagements are scoped and tailored to your specific environment, business objectives, and constraints. We do not offer generic, one-size-fits-all assessments - every engagement begins with a discovery phase to understand your unique requirements and deliver findings and recommendations that are directly actionable for your organisation.'
        },
        {
            question: 'Do you support multi-location environments?',
            answer: 'Yes. Solaris has extensive experience delivering professional services across multi-site and geographically distributed environments - including site surveys, network assessments, and security reviews spanning multiple offices, campuses, and data centres. We coordinate logistics, standardise methodologies across locations, and deliver consolidated findings with site-by-site breakdowns.'
        },
        {
            question: 'Do your assessments align to industry frameworks?',
            answer: 'Yes. Solaris assessments are benchmarked against recognised industry frameworks including ISO 27001, NIST CSF, CIS Controls, and ITIL - ensuring your organisation\'s current state is measured against established best practices. Framework alignment enables clearer gap identification, more defensible reporting, and a structured path toward compliance and certification where required.'
        },
        {
            question: 'What happens after the assessment is delivered?',
            answer: 'Following delivery of any assessment or design engagement, Solaris provides a structured findings presentation and recommendations walkthrough with your key stakeholders. Where required, we can support the implementation of recommendations through our project delivery and managed services practices - providing a seamless transition from insight to action, with a single accountable partner throughout.'
        }
    ];

    return (
        <>
            {/* ── HERO BANNER ── */}
            <section style={{
                position: 'relative',
                width: '100%',
                minHeight: '480px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,20,50,0.72)' }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '120px 15px 80px', textAlign: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 style={{
                            color: '#fff', fontWeight: 700,
                            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                            lineHeight: 1.2, margin: '0 auto 20px',
                            maxWidth: '820px'
                        }}>
                            Precision-Led Assessments for Smarter IT Decisions
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.78)', fontSize: '1rem',
                            lineHeight: 1.75, maxWidth: '520px', margin: '0 auto 36px'
                        }}>
                            Expert-led evaluations and actionable insights that help enterprises assess, optimise, and future-proof their IT environments
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <a href="#ps-overview" style={{ textDecoration: 'none' }}>
                                <button style={{
                                    display: 'inline-flex', alignItems: 'center',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    color: '#fff', border: 'none',
                                    padding: '14px 8px 14px 28px',
                                    borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                    cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: '0 8px 24px rgba(37,99,235,0.4)'
                                }}
                                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                                >
                                    <span style={{ marginRight: '16px' }}>Know More</span>
                                    <span style={{
                                        background: '#fff', borderRadius: '50%',
                                        width: '38px', height: '38px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── WHAT IS PROFESSIONAL SERVICES ── */}
            <section id="ps-overview" style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-6">
                            <div style={{ position: 'relative' }}>
                                <img
                                    src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&auto=format&fit=crop&q=80"
                                    alt="Professional Services"
                                    style={{ width: '100%', borderRadius: '16px', objectFit: 'cover', height: '400px', display: 'block' }}
                                />
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <p style={{
                                color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                WHAT IS PROFESSIONAL SERVICES?
                            </p>
                            <h2 style={{
                                fontSize: 'clamp(1.9rem, 3vw, 2rem)', fontWeight: 700,
                                color: '#0f172a', lineHeight: 1.25, marginBottom: '28px', textAlign: 'left'
                            }}>
                                Precision-Led Assessments for Smarter IT Decisions
                            </h2>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '20px', textAlign: 'left' }}>
                                Modern enterprises depend on IT environments that are secure, resilient, and continuously optimised. However, infrastructure complexity, evolving threats, and rapid digital expansion often create blind spots that impact performance and reliability.
                            </p>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '32px', textAlign: 'left' }}>
                                Solaris helps enterprises <strong style={{ color: '#0f172a' }}>assess, optimise, and future-proof their IT environments</strong> through expert-led evaluations and actionable insights that translate directly into better business outcomes.
                            </p>
                            <Link to="/contact" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '10px',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)', color: '#fff',
                                padding: '13px 28px', borderRadius: '8px', fontWeight: 700,
                                fontSize: '0.9rem', textDecoration: 'none',
                                boxShadow: '0 6px 20px rgba(37,99,235,0.3)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(37,99,235,0.45)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.3)'; }}
                            >
                                Discuss Your Professional Services Requirements
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SOLUTIONS GRID ── */}
            <section style={{ padding: '110px 0', background: '#0b1120', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', top: '-120px', left: '-120px',
                    width: '500px', height: '500px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', bottom: '-100px', right: '-100px',
                    width: '440px', height: '440px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(14,165,233,0.14) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Core Offerings
                            </span>
                        </div>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 3vw, 2rem)', fontWeight: 600,
                            color: '#fff', lineHeight: 1.2, marginBottom: '20px'
                        }}>
                            Comprehensive{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #60a5fa, #38bdf8)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                Professional Services Capabilities
                            </span>
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.75 }}>
                            End-to-end professional services expertise spanning assessments, design, security, and strategy - delivered by certified specialists with deep domain knowledge.
                        </p>
                    </div>

                    <div className="row g-4">
                        {features.map((f, i) => (
                            <div key={f.id} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -6 }}
                                    style={{
                                        background: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.09)',
                                        borderRadius: '16px', padding: '36px 30px',
                                        height: '100%', cursor: 'default',
                                        transition: 'border-color 0.3s, background 0.3s',
                                        position: 'relative', overflow: 'hidden'
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.borderColor = f.accent + '55'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                    onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 0, left: '30px', right: '30px',
                                        height: '2px', borderRadius: '0 0 4px 4px',
                                        background: `linear-gradient(90deg, ${f.accent}, transparent)`
                                    }} />
                                    <div style={{
                                        width: '52px', height: '52px', borderRadius: '12px',
                                        background: f.accent + '20', border: `1px solid ${f.accent}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: f.accent, marginBottom: '22px'
                                    }}>
                                        {f.icon}
                                    </div>
                                    <div style={{
                                        fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)',
                                        letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px'
                                    }}>{f.id}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '14px', lineHeight: 1.4 }}>{f.title}</h3>
                                    <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: 0 }}>{f.description}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SERVICES SPECTRUM ── */}
            <section style={{ position: 'relative', overflow: 'hidden', background: '#0f172a', padding: '100px 0' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&auto=format&fit=crop&q=80')`,
                    backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.18
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center g-5">
                        <div className="col-lg-5">
                            <p style={{
                                color: '#38bdf8', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                SERVICES SPECTRUM
                            </p>
                            <h2 style={{
                                color: '#fff', fontWeight: 800,
                                fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
                                lineHeight: 1.25, marginBottom: '20px'
                            }}>
                                The Complete Spectrum of Professional Services
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.85 }}>
                                Our Professional Services span critical infrastructure and security domains, delivered through structured methodologies and vendor-aligned best practices.
                            </p>
                        </div>
                        <div className="col-lg-7">
                            <div className="row g-3">
                                {servicesSpectrum.map((svc, i) => (
                                    <div key={i} className="col-md-6">
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: i * 0.07 }}
                                            viewport={{ once: true }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '14px',
                                                padding: '16px 20px', borderRadius: '10px',
                                                background: 'rgba(255,255,255,0.06)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                backdropFilter: 'blur(10px)',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseOver={e => e.currentTarget.style.background = 'rgba(56,189,248,0.1)'}
                                            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                                <path d="M5 13L9 17L19 7" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', fontWeight: 500 }}>{svc}</span>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHY CHOOSE SOLARIS ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            WHY SOLARIS
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '18px' }}>
                            Trusted Across Industries
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '1.05rem', maxWidth: '580px', margin: '0 auto' }}>
                            Delivering expert professional services with rigour, independence, and a clear focus on actionable outcomes for your business.
                        </p>
                    </div>

                    <div className="row g-4">
                        {[
                            { icon: '🔍', title: 'Independent & Objective', desc: 'Our assessments are vendor-neutral and driven solely by your business requirements - delivering honest, unbiased findings you can act on with confidence.' },
                            { icon: '🏗️', title: 'Structured Methodologies', desc: 'Every engagement follows a proven, repeatable methodology - ensuring consistent, high-quality deliverables regardless of environment size or complexity.' },
                            { icon: '🤝', title: 'Certified Domain Experts', desc: 'Our professional services teams hold industry certifications across Cisco, Microsoft, security frameworks, and cloud platforms - bringing deep expertise to every engagement.' },
                            { icon: '📋', title: 'Actionable Deliverables', desc: 'We do not deliver reports for the sake of reporting. Every Solaris assessment concludes with clear, prioritised recommendations and an implementation roadmap your teams can act on immediately.' },
                            { icon: '⚡', title: 'Rapid Time to Insight', desc: 'Structured discovery processes and experienced consultants allow us to move quickly from scoping to findings - minimising disruption and delivering value without delay.' },
                            { icon: '🔗', title: 'Seamless Transition to Delivery', desc: 'Where implementation follows assessment, Solaris provides continuity through our project delivery and managed services practices - a single partner from strategy through to operations.' }
                        ].map((item, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{
                                        padding: '32px 28px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        height: '100%', cursor: 'default'
                                    }}
                                    whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(37,99,235,0.1)', borderColor: '#93c5fd' }}
                                >
                                    <div style={{
                                        width: '56px', height: '56px', borderRadius: '14px',
                                        background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '1.6rem', marginBottom: '20px'
                                    }}>{item.icon}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: 0 }}>{item.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CASE STUDIES ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            CASE STUDIES
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '0' }}>
                            How Our Customers<br />Achieved Success
                        </h2>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <Swiper
                            modules={[Navigation, Autoplay, Pagination]}
                            spaceBetween={24}
                            slidesPerView={1}
                            navigation={{ prevEl: '.cs-ps-prev-btn', nextEl: '.cs-ps-next-btn' }}
                            pagination={{ clickable: true, el: '.cs-ps-pagination' }}
                            autoplay={{ delay: 4000, disableOnInteraction: false }}
                            breakpoints={{ 768: { slidesPerView: 2 }, 1200: { slidesPerView: 3 } }}
                            className="cs-ps-swiper pb-5"
                        >
                            {caseStudies.map((cs, i) => (
                                <SwiperSlide key={i}>
                                    <motion.div
                                        style={{
                                            borderRadius: '14px', overflow: 'hidden',
                                            background: '#fff', border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                                        }}
                                        whileHover={{ y: -6, boxShadow: '0 20px 50px rgba(0,0,0,0.12)' }}
                                    >
                                        <div style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                                            <img src={cs.image} alt={cs.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <div style={{
                                                position: 'absolute', inset: 0,
                                                background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6) 100%)'
                                            }} />
                                            {cs.dark && (
                                                <div style={{
                                                    position: 'absolute', bottom: '16px', left: '16px', right: '16px',
                                                    color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.4
                                                }}>
                                                    Discover how we transformed collaboration at scale
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '24px' }}>
                                            <span style={{
                                                fontSize: '0.75rem', fontWeight: 700, color: '#2563eb',
                                                textTransform: 'uppercase', letterSpacing: '1px',
                                                display: 'block', marginBottom: '10px'
                                            }}>+ {cs.tag}</span>
                                            <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.5, marginBottom: '20px' }}>{cs.title}</h4>
                                            <Link to="/case-study" style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                                background: '#fff', color: '#2563eb',
                                                border: '1.5px solid #2563eb', padding: '9px 20px',
                                                borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                                                textDecoration: 'none', transition: 'background 0.2s, color 0.2s'
                                            }}
                                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2563eb'; }}
                                            >
                                                Read More
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </Link>
                                        </div>
                                    </motion.div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '20px' }}>
                            <button className="cs-ps-prev-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#475569', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <div className="cs-ps-pagination" style={{ display: 'flex', gap: '6px' }} />
                            <button className="cs-ps-next-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #2563eb', background: '#2563eb', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.borderColor = '#1d4ed8'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>
                    </div>
                </div>

                <style>{`
                    .cs-ps-swiper .swiper-pagination-bullet {
                        width: 8px; height: 8px; background: #cbd5e1; opacity: 1;
                        border-radius: 50%; margin: 0 4px; transition: all 0.3s;
                    }
                    .cs-ps-swiper .swiper-pagination-bullet-active {
                        background: #2563eb; width: 24px; border-radius: 4px;
                    }
                `}</style>
            </section>

            {/* ── FAQ SECTION ── */}
            <section style={{ padding: '100px 0', background: '#0a0f1e', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(37,99,235,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.08) 0%, transparent 60%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <p style={{
                            color: '#60a5fa', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            FREQUENTLY ASKED QUESTIONS
                        </p>
                        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: '16px' }}>
                            What Would You Like to Know<br />About Professional Services
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem' }}>
                            Everything you need to know about our services and solutions
                        </p>
                    </div>

                    <div className="row g-5 align-items-start">
                        <div className="col-lg-7">
                            {faqs.map((faq, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.07 }}
                                    viewport={{ once: true }}
                                    style={{ marginBottom: '12px' }}
                                >
                                    <button
                                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                        style={{
                                            width: '100%', textAlign: 'left',
                                            background: openFaq === i ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                                            border: openFaq === i ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                            borderRadius: openFaq === i ? '12px 12px 0 0' : '12px',
                                            padding: '20px 24px',
                                            display: 'flex', alignItems: 'center', gap: '16px',
                                            cursor: 'pointer', transition: 'all 0.25s'
                                        }}
                                    >
                                        <span style={{
                                            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                                            background: openFaq === i ? 'linear-gradient(135deg,#6366f1,#4f46e5)' : 'rgba(255,255,255,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.78rem', fontWeight: 700,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.5)',
                                            transition: 'all 0.25s'
                                        }}>
                                            {i + 1}
                                        </span>
                                        <span style={{
                                            flex: 1, fontSize: '0.98rem', fontWeight: 600,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.75)',
                                            lineHeight: 1.45, textAlign: 'left'
                                        }}>
                                            {faq.question}
                                        </span>
                                        <span style={{
                                            width: '24px', height: '24px', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: openFaq === i ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                                            fontSize: '1.4rem', lineHeight: 1, fontWeight: 300,
                                            transition: 'color 0.25s'
                                        }}>
                                            {openFaq === i ? '−' : '+'}
                                        </span>
                                    </button>

                                    {openFaq === i && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            transition={{ duration: 0.25 }}
                                            style={{
                                                background: 'rgba(99,102,241,0.07)',
                                                border: '1px solid rgba(99,102,241,0.4)',
                                                borderTop: 'none',
                                                borderRadius: '0 0 12px 12px',
                                                padding: '20px 24px 24px 72px'
                                            }}
                                        >
                                            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                                                {faq.answer}
                                            </p>
                                        </motion.div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        <div className="col-lg-5">
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                style={{
                                    borderRadius: '20px', overflow: 'hidden', position: 'relative',
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)',
                                    padding: '52px 40px', textAlign: 'center',
                                    minHeight: '360px', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '30px', left: '-20px', width: '90px', height: '90px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', top: '50px', left: '20px', width: '55px', height: '55px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '-20px', right: '40px', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '28px', position: 'relative', zIndex: 1
                                }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(1.4rem, 2.5vw, 1.75rem)',
                                    lineHeight: 1.3, marginBottom: '16px', position: 'relative', zIndex: 1
                                }}>
                                    Didn't Find What You Were Looking For?
                                </h3>
                                <p style={{
                                    color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem',
                                    lineHeight: 1.7, marginBottom: '32px',
                                    position: 'relative', zIndex: 1, maxWidth: '280px'
                                }}>
                                    Our professional services team is ready to discuss your requirements and scope an engagement tailored to your needs.
                                </p>
                                <Link to="/contact" style={{ textDecoration: 'none', position: 'relative', zIndex: 1 }}>
                                    <button style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                                        background: '#fff', color: '#4f46e5',
                                        border: 'none', padding: '14px 28px',
                                        borderRadius: '50px', fontWeight: 700, fontSize: '0.92rem',
                                        cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.3)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                                    >
                                        Get In Touch With Our Experts
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 12h14M12 5l7 7-7 7" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default ProfessionalServices;
